/**
 * LeasePanel — surfaces the Lease attached to an ACCEPTED candidate.
 *
 * Phase 1 (lots 1-5):
 *   - Lease.status === 'DRAFT' + signatureMode === 'EXTERNAL_PRE_SIGNED':
 *     show the upload form (PDF file + signed date + note + attestation
 *     checkbox) → POST .../external-signed-document. Triggers
 *     the post-signature cascade server-side (User TENANT, UserColoc,
 *     TenantInfo, TenantDocument LEASE/ENROLLMENT_FILE created).
 *   - Lease.status === 'SIGNED': show the signed date, the owner-declared
 *     note, the signed PDF link, and the attestation timestamp.
 *   - Lease.status === 'CANCELLED': show "Bail annulé" with the reason.
 *   - signatureMode === 'COLOC_ESIGN': show "Mode signature électronique
 *     via Coloc — fonctionnalité à venir." (phase 2 will wire it up.)
 *
 * Lease cancellation is exposed as a button on DRAFT leases (the candidate
 * reverts to PENDING and the room is unlocked for another acceptance).
 *
 * See coloc-product-owner skill §13bis.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { leasesService } from '../services/leases.service';
import type { Lease, LeaseExternalSignedFinalizeRequest } from '../types/lease';
import {
  getLeaseStatusLabel,
} from '../types/lease';
import { formatDate } from '../utils/format';
import { getErrorMessage } from '../utils/error';
import MentionBail from './MentionBail';

const ATTESTATION_TEXT =
  "Je confirme que ce document est le bail signé par toutes les parties. " +
  "Coloc l'archive en l'état, sans constituer de dossier de preuve de signature.";

export interface LeasePanelProps {
  candidateId: string;
  /** Called after a state change that may affect the candidate list
   *  (upload finalize → CONFIRMED, cancel → PENDING). */
  onLeaseChange?: () => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'none' } // candidate has no Lease (e.g. still PENDING)
  | { kind: 'ok'; lease: Lease };

const LeasePanel: React.FC<LeasePanelProps> = ({ candidateId, onLeaseChange }) => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // Upload form local state
  const [file, setFile] = useState<File | null>(null);
  const [signedAt, setSignedAt] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [attested, setAttested] = useState<boolean>(false);
  // Décochée par défaut, comme au refus à la main (décision Y, ticket #43).
  const [notifyOthers, setNotifyOthers] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Cancel confirmation
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const lease = await leasesService.getByCandidate(candidateId);
      if (!lease) {
        setState({ kind: 'none' });
      } else {
        setState({ kind: 'ok', lease });
      }
    } catch (err) {
      setState({
        kind: 'error',
        message: getErrorMessage(err, 'Erreur lors du chargement du bail'),
      });
    }
  }, [candidateId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const canSubmit = useMemo(
    () => Boolean(file) && attested && !uploading,
    [file, attested, uploading],
  );

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state.kind !== 'ok' || !file || !attested) return;
    setUploading(true);
    setUploadError(null);
    try {
      const payload: LeaseExternalSignedFinalizeRequest = {
        attestation_confirmed: attested,
        signed_at: signedAt ? new Date(signedAt).toISOString() : undefined,
        external_signature_note: note || undefined,
        notify_rejected_candidates: notifyOthers,
      };
      await leasesService.uploadExternalSigned(state.lease.id, file, payload);
      // Clear form
      setFile(null);
      setSignedAt('');
      setNote('');
      setAttested(false);
      setNotifyOthers(false);
      await reload();
      onLeaseChange?.();
    } catch (err) {
      setUploadError(
        getErrorMessage(err, 'Erreur lors de la finalisation du bail'),
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = async () => {
    if (state.kind !== 'ok') return;
    setCancelling(true);
    setCancelError(null);
    try {
      await leasesService.cancel(state.lease.id, {
        reason: cancelReason.trim() || undefined,
      });
      setShowCancelConfirm(false);
      setCancelReason('');
      await reload();
      onLeaseChange?.();
    } catch (err) {
      setCancelError(getErrorMessage(err, "Erreur lors de l'annulation du bail"));
    } finally {
      setCancelling(false);
    }
  };

  if (state.kind === 'loading') {
    return <div className="lease-panel lease-panel-loading">Chargement du bail...</div>;
  }
  if (state.kind === 'error') {
    return <div className="lease-panel candidats-error">{state.message}</div>;
  }
  if (state.kind === 'none') {
    // Defensive: ACCEPTED candidate without a Lease shouldn't happen in
    // phase 1+. Render nothing to avoid noise in earlier-state rows.
    return null;
  }

  const { lease } = state;
  // Il n'existe plus qu'une façon d'obtenir un bail signé : hors de Coloc,
  // puis téléversé. La signature électronique via Coloc a été abandonnée.
  const isExternal = true;
  // DRAFT **ou** PENDING_SIGNATURE : depuis la reservation, un bail nait en
  // PENDING_SIGNATURE. Garder le televersement sur le seul DRAFT le rendait
  // inatteignable — le proprietaire ne pouvait plus deposer le bail signe,
  // donc plus jamais ouvrir l'acces de son locataire.
  const isDraft = lease.status === 'DRAFT' || lease.status === 'PENDING_SIGNATURE';
  const isSigned = lease.status === 'SIGNED';
  const isCancelled = lease.status === 'CANCELLED';

  return (
    <div className="lease-panel" data-testid="lease-panel">
      <div className="lease-panel-header">
        <h4>Bail</h4>
        <span
          className={`lease-status lease-status-${lease.status.toLowerCase()}`}
          data-testid="lease-status"
        >
          {getLeaseStatusLabel(lease.status)}
        </span>
      </div>

      <div className="lease-panel-meta">
        <div className="detail-item">
        </div>
        <div className="detail-item">
          <span>Date de début de bail</span>
          <span>{formatDate(lease.leaseStartDate)}</span>
        </div>
        <div className="detail-item">
          <span>Loyer HC</span>
          <span>{lease.monthlyRent} €</span>
        </div>
        {lease.chargesProvision != null && (
          <div className="detail-item">
            <span>Provision charges</span>
            <span>{lease.chargesProvision} €</span>
          </div>
        )}
      </div>

      {/* ── Mode COLOC_ESIGN — phase 2, not wired yet ──────────────────── */}
      {!isExternal && (
        <div className="lease-panel-info">
          La signature électronique via Coloc n&apos;est pas encore disponible.
          Elle sera activée dans une prochaine mise à jour.
        </div>
      )}

      {/* ── Mode EXTERNAL — DRAFT: upload form ─────────────────────────── */}
      {isExternal && isDraft && (
        <form className="lease-upload-form" onSubmit={handleUpload}>
          <p className="form-hint-block">
            Le propriétaire a obtenu le bail signé par toutes les parties
            (agence, signature électronique externe, papier scanné…). Téléversez
            le PDF signé pour finaliser le bail : il rejoint le dossier du locataire.
          </p>

          <div className="form-group">
            <label htmlFor={`lease-file-${candidateId}`}>Bail signé (PDF) *</label>
            <input
              id={`lease-file-${candidateId}`}
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="lease-file-input"
            />
            {file && (
              <small className="form-hint">
                {file.name} ({Math.round(file.size / 1024)} ko)
              </small>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor={`lease-signed-at-${candidateId}`}>
                Date de signature
              </label>
              <input
                id={`lease-signed-at-${candidateId}`}
                type="date"
                className="form-control"
                value={signedAt}
                onChange={(e) => setSignedAt(e.target.value)}
              />
              <small className="form-hint">Laissez vide pour utiliser la date d&apos;aujourd&apos;hui.</small>
            </div>

            <div className="form-group">
              {/* Le champ « mot de passe initial du locataire » a été retiré :
                  le bailleur ne choisit plus l'accès de son locataire, qui le
                  définit lui-même via un lien reçu par email. */}
              <p className="form-hint">
                Le locataire recevra un email l&apos;invitant à créer son mot de
                passe. Vous ne le connaîtrez pas.
              </p>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor={`lease-note-${candidateId}`}>Note (optionnel)</label>
            <textarea
              id={`lease-note-${candidateId}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Ex : Bail Foncia signé en agence le 28/05/2026"
              className="form-control"
            />
          </div>

          <div className="form-group lease-attestation">
            <label>
              <input
                type="checkbox"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
                data-testid="lease-attestation-checkbox"
              />{' '}
              {ATTESTATION_TEXT}
            </label>
          </div>

          {/* `action-choix` (socle) et non `form-group` : une règle globale
              `.form-group input { width: 100% }` étirerait la case. */}
          <div className="action-choix">
            <label>
              <input
                type="checkbox"
                checked={notifyOthers}
                onChange={(e) => setNotifyOthers(e.target.checked)}
                data-testid="lease-notify-others-checkbox"
              />
              Prévenir les autres candidats de cette chambre
            </label>
            <small className="form-hint">
              À la signature, les autres candidats de la chambre sont refusés et
              leurs pièces supprimées. Cochée, chacun reçoit votre email de refus.
            </small>
          </div>

          {uploadError && (
            <div className="candidats-error">{uploadError}</div>
          )}

          <div className="lease-panel-actions">
            <button
              type="submit"
              className="btn-action btn-save"
              disabled={!canSubmit}
              data-testid="lease-submit-button"
            >
              {uploading ? 'Finalisation en cours...' : 'Confirmer la prise d\'effet du bail'}
            </button>

            {!showCancelConfirm ? (
              <button
                type="button"
                className="btn-action btn-secondary"
                onClick={() => setShowCancelConfirm(true)}
              >
                Annuler le bail
              </button>
            ) : (
              <span className="lease-cancel-confirm">
                <input
                  type="text"
                  className="form-control"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Raison (optionnel)"
                  style={{ maxWidth: '220px' }}
                />
                <button
                  type="button"
                  className="btn-action btn-danger"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? '...' : 'Confirmer'}
                </button>
                <button
                  type="button"
                  className="btn-action btn-secondary"
                  onClick={() => {
                    setShowCancelConfirm(false);
                    setCancelReason('');
                    setCancelError(null);
                  }}
                >
                  Non
                </button>
              </span>
            )}
          </div>
          {cancelError && (
            <div className="candidats-error">{cancelError}</div>
          )}
        </form>
      )}

      {/* ── Mode EXTERNAL — SIGNED: archive view ───────────────────────── */}
      {isExternal && isSigned && (
        <div className="lease-signed-view">
          <div className="detail-item">
            <span>Date de signature</span>
            <span>{lease.signedAt ? formatDate(lease.signedAt) : '—'}</span>
          </div>
          <div className="detail-item">
            <span>Attestation owner</span>
            <span>
              {lease.externalSignatureAttestedAt
                ? formatDate(lease.externalSignatureAttestedAt)
                : '—'}
            </span>
          </div>
          {lease.externalSignatureNote && (
            <div className="detail-item">
              <span>Note</span>
              <span>{lease.externalSignatureNote}</span>
            </div>
          )}
          <p className="form-hint">
            Le bail est rangé dans le dossier du locataire.
          </p>
        </div>
      )}

      {/* ── CANCELLED: archive view ────────────────────────────────────── */}
      {isCancelled && (
        <div className="lease-cancelled-view">
          <div className="detail-item">
            <span>Annulé le</span>
            <span>{lease.cancelledAt ? formatDate(lease.cancelledAt) : '—'}</span>
          </div>
          {lease.cancellationReason && (
            <div className="detail-item">
              <span>Raison</span>
              <span>{lease.cancellationReason}</span>
            </div>
          )}
        </div>
      )}

      <MentionBail />
    </div>
  );
};

export default LeasePanel;
